import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import type { SessionUser } from '@smile/shared';
import { api } from '../api/client';
import { ROLE_LABEL } from '../lib/format';
import { can } from '../lib/perms';
import { useLive } from '../lib/live';
import { MessagesBell } from './MessagesBell';
import { WhatsAppBell } from './WhatsAppBell';

/**
 * The navigation follows the legacy extranet where it can, and this comment
 * used to claim "same items, same order, same Czech labels". That was not
 * true, and it is the kind of untrue that matters here, because the whole
 * constraint on this project is that nobody can be retrained.
 *
 * What the legacy menu actually reads, top to bottom: Rezervace, Převozy,
 * Ceník, Uzavírky, Články, Doplňkové služby, Interní zprávy, Uživatelé,
 * Smile Parking Club členi. What the approved mockups draw: Dashboard,
 * Rezervace, Převozy, Ceník, Nastavení, Reporty.
 *
 * So of the seven items below, three are faithful (Rezervace, Převozy,
 * Nastavení), one comes from the mockups and not the legacy (Dashboard), and
 * three are words that appear in neither:
 *
 *   Klienti     — the legacy calls this "Smile Parking Club členi", and the
 *                 page it opens still carries the legacy heading, "Smile
 *                 Parking členové". A manager looking for the club members
 *                 is looking for the word "Club".
 *   Směny       — the legacy menu's "Uzavírky" is parking closures, a
 *                 different thing, so there is no legacy label to copy here.
 *   Statistiky  — the mockups say "Reporty".
 *
 * None of these is changed unilaterally: what the menu says is the owner's
 * decision, not a refactor, and it is in the report for him. What is fixed is
 * the claim — a comment asserting fidelity is how a divergence stops being
 * noticed.
 *
 * ASKED AND ANSWERED, 26 July 2026. The owner was shown the three divergences
 * above and said to leave them: "don't change the names on menus". So the
 * labels below are now a decision rather than an oversight, and the three
 * words that appear in neither the legacy menu nor the mockups stay. The
 * paragraphs above are kept as the record of what was asked, not as a list of
 * things still to fix — anybody minded to "correct" Statistiky to Reporty for
 * fidelity's sake should raise it with him first rather than with this file.
 *
 * Two consequences worth carrying: the members page still opens under the
 * legacy heading "Smile Parking členové" while the menu says Klienti, which is
 * now a deliberate mismatch rather than a loose end; and Směny has no legacy
 * label at all, so it is the one item here nobody's muscle memory can help
 * with. That is an argument for watching where operators hesitate when the
 * screens are shown to them, not for renaming it now.
 */
/* Each item shows only to someone who may open it (the owner, 30 October 2026:
   "hide menu items a person can't open"); `need` is any one of these grants.
   The server still refuses on its own — this only stops the dead click. */
type Need = Array<[string, 'read' | 'write']>;
const ITEMS: Array<{ to: string; label: string; end?: boolean; need: Need }> = [
  { to: '/', label: 'Dashboard', end: true, need: [['/dashboard/*', 'read']] },
  { to: '/rezervace', label: 'Rezervace', need: [['/reservations/*', 'read']] },
  { to: '/prevozy', label: 'Převozy', need: [['/transfers/*', 'read']] },
  // Added 29 October 2026 on the owner's word ("New menu item Přílety"): real arrival times from the airport.
  { to: '/prilety', label: 'Přílety', need: [['/transfers/*', 'read']] },
  { to: '/klienti', label: 'Klienti', need: [['/clients/*', 'read']] },
  // Added 29 October 2026 on the owner's word ("Add a menu item too"): the WhatsApp chat page.
  { to: '/whatsapp', label: 'WhatsApp', need: [['/chats/*', 'read']] },
  { to: '/smeny', label: 'Směny', need: [['/balances/*', 'read']] },
  { to: '/statistiky', label: 'Statistiky', need: [['/reports/*', 'read']] },
  { to: '/nastaveni', label: 'Nastavení', need: [['/catalog/*', 'read']] },
];

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

export function Navbar({ user }: { user: SessionUser }) {
  const live = useLive();
  const [open, setOpen] = useState(false);
  const [signOut, setSignOut] = useState<'idle' | 'busy' | 'failed'>('idle');
  const menuRef = useRef<HTMLDivElement>(null);

  /* The account control used to be a bare <span onClick={logout}>: one
     stray click on your own name and the shift ended. It is now a menu —
     signing out takes a deliberate second click, and because it is a real
     button it is also reachable from the keyboard. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggleTheme = () => {
    const el = document.documentElement;
    const next = el.dataset.theme === 'dark' ? 'light' : 'dark';
    el.dataset.theme = next;
    try {
      localStorage.setItem('smile-theme', next);
    } catch {
      /* private mode — theme just will not persist */
    }
  };

  /**
   * Signing out, and what happens when the server says no.
   *
   * The previous version of this was three lines and two bugs. It sent the
   * request through `.catch(() => undefined)`, so a refusal became silence;
   * and it called `navigate()` and then `window.location.reload()` in the same
   * breath, so whichever URL won the race is the one that reloaded. Between
   * them, a sign-out the API had rejected looked exactly like a page refresh —
   * which is what it was, with the session cookie still on it.
   *
   * The cookie is httpOnly. Only the server can remove it, so a sign-out the
   * server did not perform is a sign-out that did not happen, and there is
   * nothing this component can do to finish the job. Saying so is the fix.
   * Anything that clears local state and shows the login screen anyway would
   * be worse than the old behaviour, not better: the person would believe they
   * had signed out on a machine where their session is still live.
   */
  const logout = async () => {
    setSignOut('busy');
    try {
      await api.logout();
    } catch {
      setSignOut('failed');
      return;
    }
    /* One navigation rather than two, and a full document load rather than a
       route change: it drops every cached query along with the session rather
       than leaving the previous user's rows in memory, which is what the
       reload was for. `replace` keeps the signed-in page out of the back
       button. */
    window.location.replace('/prihlaseni');
  };

  const name = user.name || user.email;

  return (
    <nav className="navbar">
      <div className="brand">
        <div className="mark">SP</div>
        <div>
          <b>Smile Parking</b>
          <small>Extranet</small>
        </div>
      </div>

      {/* `display: contents` on a computer (the items sit in the bar as before); on a phone the
          row of its own that scrolls sideways (the manager, 30 October 2026: on an iPhone the bar
          was 1 209 px wide and the whole page shrank to a third of the screen). */}
      <div className="navitems">
        {ITEMS.filter((it) => it.need.some(([obj, act]) => can(user, obj, act))).map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            className={({ isActive }) => (isActive ? 'item active' : 'item')}
          >
            {it.label}
          </NavLink>
        ))}
      </div>

      <div className="right">
        <span className={`live ${live.state}`} title={live.title} role="status">
          <span className="dot" aria-hidden="true" /> {live.label}
        </span>

        <WhatsAppBell />
        <MessagesBell />

        <button className="themebtn" onClick={toggleTheme} title="Světlý / tmavý režim">
          ◐
        </button>

        <div className="usermenu" ref={menuRef}>
          <button
            className="user"
            type="button"
            onClick={() => {
              // A failure belongs to the attempt, not to the menu.
              setSignOut('idle');
              setOpen((o) => !o);
            }}
            aria-haspopup="menu"
            aria-expanded={open}
            title={user.email}
          >
            <span className="avatar" aria-hidden="true">{initials(name)}</span>
            <span className="uname">{name}</span>
            <span className="caret" aria-hidden="true">▾</span>
          </button>

          {open && (
            <div className="menu" role="menu">
              <div className="mhead">
                <b>{name}</b>
                <span>{user.email}</span>
                <span className="mrole">{ROLE_LABEL[user.role] ?? user.role}</span>
              </div>
              <Link className="mitem" role="menuitem" to="/ucet" onClick={() => setOpen(false)}>
                Můj účet
              </Link>
              <button
                className="mitem danger"
                type="button"
                role="menuitem"
                onClick={logout}
                disabled={signOut === 'busy'}
              >
                {signOut === 'busy' ? 'Odhlašuji…' : 'Odhlásit se'}
              </button>
              {signOut === 'failed' && (
                <div className="mnote" role="alert">
                  Odhlášení se nezdařilo — zůstáváte přihlášeni. Zkuste to prosím
                  znovu.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
